/**
 * SistemSablonlari — E-posta Şablonları › "Sistem şablonları" sekmesi (backlog #573, backend #567).
 *
 * Sistemin KENDİ gönderdiği postalar (ACTIVATION, FORGOT_PASSWORD, ...): liste backend'in kayıt defterinden gelir (GET /email/templates/system), bu
 * yüzden sonradan eklenen bir posta sayfada hiçbir değişiklik istemez. Şablon eklenmez, silinmez, anahtarı değişmez. Düzenlenen metin: konu,
 * başlık satırı, gövde (CKEditor) ve düğme yazısı, Türkçe ve İngilizce yan yana; marka, renkler, düğmenin kendisi ve altındaki bağlantı SABİT bir düzenin
 * (backend EmailLayout) içindedir, editörden bozulamaz. İngilizce alıcıya İngilizce YALNIZCA konu, gövde ve düğme üçü de doluysa gider, yoksa postanın tamamı Türkçe.
 *
 * Yer tutucular ({{ad}}, {{link}} ...): en son tıklanan alana eklenir; {{link}} konuya konamaz (konu posta kaydına olduğu gibi yazılır, bağlantı orada gizlenemez).
 * Kaydet, bilinmeyen / bozuk yer tutucuyu ve boş zorunlu alanı backend'in kendi metniyle reddeder; metin değiştirmek SYSTEM_SETTINGS_MANAGE ister.
 * "Önizle" postayı örnek değerlerle sabit düzenin içinde gösterir, "Bana test gönder" onu giriş yapan personelin KENDİ adresine yollar (güvenli mod geçerlidir).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  Autoformat, AutoLink, Bold, ClassicEditor, Essentials, GeneralHtmlSupport, Image, ImageInsert, ImageResize, ImageStyle, ImageTextAlternative, ImageToolbar,
  ImageUpload, Italic, Link, Paragraph, SourceEditing, Underline, Undo,
} from 'ckeditor5';
import { useTranslation } from 'react-i18next';
import { PERMISSION_KEYS, useYetki } from '../../../../../services/rbacService';
import { listSystemTemplates, previewTemplate, sendTemplateTest, updateSystemTemplate } from '../../../../../services/emailAyarService';
import { EpostaResimYuklemeEklentisi } from '../../../../../services/epostaResimYukle';

const TR = 1;
const EN = 2;

// Gövde editörü: sabit düzenin içinde duran kısa bir metin, o yüzden küçük bir araç çubuğu. Backend HTML'i güvenilir sayar (eskiden de öyleydi): hiçbir şey ayıklanmaz.
const EDITOR_CONFIG = {
  // WAS (until 2026-10-30): no image button and no image plugins
  toolbar: { items: ['undo', 'redo', '|', 'bold', 'italic', 'underline', '|', 'link', 'insertImage', '|', 'sourceEditing'], shouldNotGroupWhenFull: true },
  plugins: [
    Essentials, Paragraph, Autoformat, Bold, Italic, Underline, Link, AutoLink, Undo, SourceEditing, GeneralHtmlSupport,
    Image, ImageInsert, ImageResize, ImageStyle, ImageTextAlternative, ImageToolbar, ImageUpload,
  ],
  extraPlugins: [EpostaResimYuklemeEklentisi],
  htmlSupport: { allow: [{ name: /^.*$/, styles: true, attributes: true, classes: true }] },
  image: { upload: { types: ['jpeg', 'png', 'gif', 'webp'] }, toolbar: ['imageTextAlternative', '|', 'imageStyle:inline', '|', 'resizeImage'] },
  link: { defaultProtocol: 'https://' },
};

// Alanın form anahtarı: dil + alan
const ALAN = {
  tr: { subject: 'title', headline: 'headline', body: 'content', button: 'button' },
  en: { subject: 'titleEn', headline: 'headlineEn', body: 'contentEn', button: 'buttonEn' },
};

function formdan(tpl) {
  return {
    title: tpl.email_template_title || '',
    headline: tpl.email_template_headline || '',
    content: tpl.email_template_content || '',
    button: tpl.email_template_button || '',
    titleEn: tpl.email_template_title_en || '',
    headlineEn: tpl.email_template_headline_en || '',
    contentEn: tpl.email_template_content_en || '',
    buttonEn: tpl.email_template_button_en || '',
  };
}

const dolu = (s) => typeof s === 'string' && s.trim() !== '';
const ingilizceHazir = (tpl) => dolu(tpl.email_template_title_en) && dolu(tpl.email_template_content_en) && dolu(tpl.email_template_button_en);

const SistemSablonlari = () => {
  const { t } = useTranslation('moduller');
  const duzenleyebilir = useYetki(PERMISSION_KEYS.SYSTEM_SETTINGS_MANAGE) === true;

  const [liste, setListe] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [listeHatasi, setListeHatasi] = useState(null);
  const [yenile, setYenile] = useState(0);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const d = await listSystemTemplates();
        if (!ignore) { setListe(d); setListeHatasi(null); }
      } catch (e) {
        if (!ignore) setListeHatasi(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [yenile]);

  // ── düzenleme ───────────────────────────────────────────────────────────────────────────────────
  const [duzenle, setDuzenle] = useState(null);       // düzenlenen şablon
  const [form, setForm] = useState(null);
  const [kayitHatasi, setKayitHatasi] = useState(null);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [odak, setOdak] = useState('tr.subject');      // "dil.alan": yer tutucu en son buraya eklenir
  const girdiler = useRef({});                         // "dil.alan" → <input>
  const editorlar = useRef({});                        // "tr" | "en" → CKEditor

  function duzenlemeyiAc(tpl) {
    setDuzenle(tpl);
    setForm(formdan(tpl));
    setKayitHatasi(null);
    setOdak('tr.subject');
  }

  function duzenlemeyiKapat() {
    setDuzenle(null);
    setForm(null);
    editorlar.current = {};
    girdiler.current = {};
  }

  function yerTutucuEkle(token) {
    const [dil, alan] = odak.split('.');
    if (alan === 'body') {
      const ed = editorlar.current[dil];
      if (ed) {
        ed.model.change((writer) => { ed.model.insertContent(writer.createText(token)); });
        ed.editing.view.focus();
      }
      return;
    }
    const anahtar = ALAN[dil][alan];
    const el = girdiler.current[odak];
    const deger = form[anahtar] || '';
    const bas = el && typeof el.selectionStart === 'number' ? el.selectionStart : deger.length;
    const son = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : deger.length;
    setForm((f) => ({ ...f, [anahtar]: deger.slice(0, bas) + token + deger.slice(son) }));
  }

  async function kaydet() {
    setKaydediyor(true);
    setKayitHatasi(null);
    try {
      await updateSystemTemplate(duzenle.email_template_id, form);
      duzenlemeyiKapat();
      setYenile((n) => n + 1);
    } catch (e) {
      setKayitHatasi(e.message);   // backend'in kendi metni: bilinmeyen yer tutucu, boş alan, yetki
    } finally {
      setKaydediyor(false);
    }
  }

  // ── önizleme ve test ────────────────────────────────────────────────────────────────────────────
  const [onizleme, setOnizleme] = useState(null);      // { tpl, dil, veri, hata, yukleniyor }
  const [testSonucu, setTestSonucu] = useState(null);  // { anahtar, status, message, error } | { anahtar, status: 'ERROR', message }
  const [testIsteniyor, setTestIsteniyor] = useState(null);

  async function onizlemeAc(tpl, dil) {
    setOnizleme({ tpl, dil, veri: null, hata: null, yukleniyor: true });
    try {
      const veri = await previewTemplate(tpl.email_template_id, dil);
      setOnizleme({ tpl, dil, veri, hata: null, yukleniyor: false });
    } catch (e) {
      setOnizleme({ tpl, dil, veri: null, hata: e.message, yukleniyor: false });
    }
  }

  async function testGonder(tpl, dil) {
    const anahtar = `${tpl.template_key}.${dil}`;
    setTestIsteniyor(anahtar);
    try {
      const r = await sendTemplateTest(tpl.email_template_id, dil);
      setTestSonucu({ anahtar: tpl.template_key, status: r.status, message: r.message, error: r.error });
    } catch (e) {
      setTestSonucu({ anahtar: tpl.template_key, status: 'ERROR', message: e.message, error: null });
    } finally {
      setTestIsteniyor(null);
    }
  }

  const testRengi = (s) => (s === 'SENT' ? 'success' : s === 'BLOCKED' ? 'warning' : 'danger');

  // ── editörün bir dilinin sütunu ─────────────────────────────────────────────────────────────────
  function dilSutunu(dil, baslik) {
    const a = ALAN[dil];
    const alanGirdisi = (alan, etiket, maks) => (
      <div className="mb-3">
        <Label className="form-label" htmlFor={`ss-${dil}-${alan}`}>{etiket}</Label>
        <Input
          id={`ss-${dil}-${alan}`} type="text" maxLength={maks} value={form[a[alan]]} disabled={!duzenleyebilir}
          innerRef={(el) => { girdiler.current[`${dil}.${alan}`] = el; }}
          onFocus={() => setOdak(`${dil}.${alan}`)}
          onChange={(e) => setForm((f) => ({ ...f, [a[alan]]: e.target.value }))}
        />
      </div>
    );
    return (
      <Col lg={6} key={dil}>
        <h6 className="text-body border-bottom pb-2 mb-3">{baslik}</h6>
        {alanGirdisi('subject', t('emailsSistem.konu'), 255)}
        {alanGirdisi('headline', t('emailsSistem.baslikSatiri'), 255)}
        <div className="mb-3">
          <Label className="form-label">{t('emailsSistem.metin')}</Label>
          <CKEditor
            editor={ClassicEditor}
            config={EDITOR_CONFIG}
            data={form[a.body]}
            disabled={!duzenleyebilir}
            onReady={(ed) => {
              editorlar.current[dil] = ed;
              ed.editing.view.document.on('change:isFocused', (_e, _n, odakta) => { if (odakta) setOdak(`${dil}.body`); });
            }}
            onChange={(_e, ed) => { const veri = ed.getData(); setForm((f) => (f[a.body] === veri ? f : { ...f, [a.body]: veri })); }}
          />
        </div>
        {alanGirdisi('button', t('emailsSistem.dugmeYazisi'), 100)}
      </Col>
    );
  }

  const konudaMi = odak.endsWith('.subject');

  return (
    <>
      <p className="text-muted">{t('emailsSistem.aciklama')}</p>
      {listeHatasi && <Alert color="danger" className="py-2">{listeHatasi}</Alert>}
      {testSonucu && (
        <Alert color={testRengi(testSonucu.status)} className="py-2" toggle={() => setTestSonucu(null)}>
          <strong>{testSonucu.anahtar}: </strong>{testSonucu.message}
          {testSonucu.error && <span className="d-block small text-muted">{testSonucu.error}</span>}
        </Alert>
      )}
      {yukleniyor ? (
        <div><Spinner size="sm" className="me-2" />{t('common:yukleniyor')}</div>
      ) : (
        <Row>
          {(liste || []).map((tpl) => (
            <Col xl={6} key={tpl.template_key}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 className="mb-1"><code>{tpl.template_key}</code></h5>
                      <p className="text-muted mb-0">{tpl.description}</p>
                    </div>
                    {ingilizceHazir(tpl)
                      ? <Badge color="success" className="ms-2">{t('emailsSistem.ingilizceHazir')}</Badge>
                      : <Badge color="warning" className="ms-2" title={t('emailsSistem.ingilizceEksikAciklama')}>{t('emailsSistem.ingilizceEksik')}</Badge>}
                  </div>
                  <dl className="row mb-3 small">
                    <dt className="col-sm-3">{t('emailsSistem.konu')} (TR)</dt>
                    <dd className="col-sm-9">{tpl.email_template_title}</dd>
                    <dt className="col-sm-3">{t('emailsSistem.konu')} (EN)</dt>
                    <dd className="col-sm-9">{tpl.email_template_title_en || <span className="text-muted">{t('emailsSistem.yok')}</span>}</dd>
                    <dt className="col-sm-3">{t('emailsSistem.dugmeYazisi')}</dt>
                    <dd className="col-sm-9">{tpl.email_template_button}{tpl.email_template_button_en ? ` / ${tpl.email_template_button_en}` : ''}</dd>
                  </dl>
                  <div className="d-flex flex-wrap gap-2">
                    <Button color="warning" disabled={!duzenleyebilir} title={duzenleyebilir ? undefined : t('emailsSistem.yetkiYok')} onClick={() => duzenlemeyiAc(tpl)}>
                      {t('common:duzenle')}
                    </Button>
                    <ButtonGroup>
                      <Button color="light" onClick={() => onizlemeAc(tpl, TR)}>{t('emailsSistem.onizleTr')}</Button>
                      <Button color="light" onClick={() => onizlemeAc(tpl, EN)}>{t('emailsSistem.onizleEn')}</Button>
                    </ButtonGroup>
                    <ButtonGroup>
                      <Button color="primary" outline disabled={testIsteniyor !== null} onClick={() => testGonder(tpl, TR)}>
                        {testIsteniyor === `${tpl.template_key}.${TR}` ? <Spinner size="sm" /> : t('emailsSistem.testTr')}
                      </Button>
                      <Button color="primary" outline disabled={testIsteniyor !== null} onClick={() => testGonder(tpl, EN)}>
                        {testIsteniyor === `${tpl.template_key}.${EN}` ? <Spinner size="sm" /> : t('emailsSistem.testEn')}
                      </Button>
                    </ButtonGroup>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* düzenleme */}
      <Modal isOpen={Boolean(duzenle)} toggle={duzenlemeyiKapat} size="xl" scrollable>
        <ModalHeader toggle={duzenlemeyiKapat}>{duzenle ? t('emailsSistem.duzenleBaslik', { anahtar: duzenle.template_key }) : ''}</ModalHeader>
        <ModalBody>
          {duzenle && form && (
            <>
              <p className="text-muted small">{t('emailsSistem.duzenAciklamasi')}</p>
              <div className="mb-3">
                <span className="me-2 fw-semibold">{t('emailsSistem.yerTutucular')}:</span>
                {(duzenle.placeholders || []).map((p) => {
                  const yasak = konudaMi && !p.allowed_in_subject;
                  return (
                    <Button
                      key={p.name} size="sm" color="soft-primary" className="me-1 mb-1 font-monospace" disabled={!duzenleyebilir || yasak}
                      title={yasak ? t('emailsSistem.konuyaLinkOlmaz') : p.description}
                      onClick={() => yerTutucuEkle(p.token)}
                    >
                      {p.token}
                    </Button>
                  );
                })}
                <div className="small text-muted">{t('emailsSistem.yerTutucuIpucu')}</div>
              </div>
              {kayitHatasi && <Alert color="danger" className="py-2">{kayitHatasi}</Alert>}
              <Row>
                {dilSutunu('tr', t('emailsSistem.turkce'))}
                {dilSutunu('en', t('emailsSistem.ingilizce'))}
              </Row>
              <p className="text-muted small mb-0">{t('emailsSistem.ingilizceKurali')}</p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" disabled={kaydediyor || !duzenleyebilir} onClick={kaydet}>
            {kaydediyor ? <Spinner size="sm" /> : t('common:kaydet')}
          </Button>
          <Button color="light" onClick={duzenlemeyiKapat}>{t('common:iptal')}</Button>
        </ModalFooter>
      </Modal>

      {/* önizleme */}
      <Modal isOpen={Boolean(onizleme)} toggle={() => setOnizleme(null)} size="lg">
        <ModalHeader toggle={() => setOnizleme(null)}>{onizleme ? t('emailsSistem.onizlemeBaslik', { anahtar: onizleme.tpl.template_key }) : ''}</ModalHeader>
        <ModalBody>
          {onizleme && (
            <>
              <ButtonGroup className="mb-3">
                <Button color={onizleme.dil === TR ? 'primary' : 'light'} onClick={() => onizlemeAc(onizleme.tpl, TR)}>{t('emailsSistem.turkce')}</Button>
                <Button color={onizleme.dil === EN ? 'primary' : 'light'} onClick={() => onizlemeAc(onizleme.tpl, EN)}>{t('emailsSistem.ingilizce')}</Button>
              </ButtonGroup>
              {onizleme.yukleniyor && <div><Spinner size="sm" className="me-2" />{t('common:yukleniyor')}</div>}
              {onizleme.hata && <Alert color="danger">{onizleme.hata}</Alert>}
              {onizleme.veri?.render_error && <Alert color="danger">{onizleme.veri.render_error}</Alert>}
              {onizleme.veri && !onizleme.veri.render_error && (
                <>
                  {onizleme.dil === EN && onizleme.veri.language_id !== EN && <Alert color="warning" className="py-2">{t('emailsSistem.onizlemeTurkceGider')}</Alert>}
                  <p className="mb-2"><span className="text-muted">{t('emailsSistem.konu')}: </span><strong>{onizleme.veri.subject}</strong></p>
                  <iframe
                    title={t('emailsSistem.onizlemeIframe')} sandbox="" srcDoc={onizleme.veri.html || ''}
                    style={{ width: '100%', minHeight: 460, border: '1px solid var(--vz-border-color, #dee2e6)', borderRadius: 4, background: '#fff' }}
                  />
                  <p className="text-muted small mt-2 mb-0">{t('emailsSistem.onizlemeOrnek')}</p>
                </>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default SistemSablonlari;
